import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { setLenis, getLenis } from "@/lib/lenis";
import { RouteSeo } from "@/lib/seo";
import { themeFor, BLUE_PREVIEW_PATH } from "@/lib/theme";
import ErrorBoundary from "@/components/ErrorBoundary";

import Home from "@/pages/site/Home";
import HomeBlue from "@/pages/site/HomeBlue";
import HomePan from "@/pages/site/HomePan";
import StaffGate from "@/pages/site/StaffGate";
import Accept from "@/pages/site/Accept";
import SolutionsIndex from "@/pages/site/SolutionsIndex";
import SolutionDetail from "@/pages/site/SolutionDetail";
import ServicesIndex from "@/pages/site/ServicesIndex";
import ServiceDetail from "@/pages/site/ServiceDetail";
import IndustriesIndex from "@/pages/site/IndustriesIndex";
import IndustryDetail from "@/pages/site/IndustryDetail";
import Projects from "@/pages/site/Projects";
import ProjectDetail from "@/pages/site/ProjectDetail";
import About from "@/pages/site/About";
import Insights from "@/pages/site/Insights";
import ArticleDetail from "@/pages/site/ArticleDetail";
import Support from "@/pages/site/Support";
import Contact from "@/pages/site/Contact";
import Careers from "@/pages/site/Careers";
import CareerReplace from "@/pages/site/CareerReplace";
import Resubmit from "@/pages/site/Resubmit";
import HoldUpload from "@/pages/site/HoldUpload";
import DocRequestUpload from "@/pages/site/DocRequestUpload";
import Admin from "@/pages/site/Admin";
import Consultation from "@/pages/site/Consultation";
import NotFound from "@/pages/site/NotFound";
import Technology from "@/pages/site/Technology";
import Downloads from "@/pages/site/Downloads";
import Legal from "@/pages/site/Legal";
import RequestQuote from "@/pages/site/RequestQuote";
import Inquiries from "@/pages/site/Inquiries";
import Team from "@/pages/site/Team";
import Faq from "@/pages/site/Faq";
import Brands from "@/pages/site/Brands";
import ThankYou from "@/pages/site/ThankYou";
import PaymentResult from "@/pages/site/PaymentResult";
import PaymentLink from "@/pages/site/PaymentLink";
import Receipts from "@/pages/site/Receipts";
import Cart from "@/pages/site/Cart";
import PaymentTest from "@/pages/site/PaymentTest";
import Store from "@/pages/site/Store";
import StoreCategory from "@/pages/site/StoreCategory";
import TrackOrder from "@/pages/site/TrackOrder";
import Webmail from "@/pages/site/Webmail";
import ConnectApp from "@/pages/connect/ConnectApp";
import ConnectPrivacy from "@/pages/connect/ConnectPrivacy";
import ConnectDeleteAccount from "@/pages/connect/ConnectDeleteAccount";
import OnboardingPortal from "@/pages/site/OnboardingPortal";
import RegularizationRequest from "@/pages/site/RegularizationRequest";
import LeaveRequest from "@/pages/site/LeaveRequest";
import FieldDuty from "@/pages/site/FieldDuty";
import MyRequests from "@/pages/site/MyRequests";

import ConceptPicker from "@/pages/ConceptPicker";
import ConceptTwo from "@/pages/ConceptTwo";
import ConceptThree from "@/pages/ConceptThree";
import { ChatLauncher } from "./components/site/ChatLauncher";
import { EnquiryChatModal } from "./components/site/EnquiryChatModal";
import { BackToTop } from "@/components/site/BackToTop";
import { CookieConsent } from "@/components/site/CookieConsent";

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const theme = themeFor(pathname);
    document.documentElement.classList.toggle("nx-theme-blue", theme === "blue");
    document.documentElement.classList.toggle("nx-theme-pan", theme === "pan");
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Anonymous page-view counter — no cookies, no personal data; a random id in the
// browser lets us count unique visitors. Admin/concept pages are never tracked.
const PageTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin") || pathname.startsWith("/concept") || pathname.startsWith("/onboarding")) return;
    try {
      let vid = localStorage.getItem("nx_vid");
      if (!vid) {
        vid = crypto.randomUUID();
        localStorage.setItem("nx_vid", vid);
      }
      axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/track`, { path: pathname, vid }).catch(() => {});
    } catch { /* tracking is best-effort only */ }
  }, [pathname]);
  return null;
};

const PublicChrome = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin") || pathname.startsWith("/connect") || pathname.startsWith("/privacy-policy") || pathname.startsWith("/delete-account") || pathname.startsWith("/onboarding") || pathname.startsWith("/attendance-correction") || pathname.startsWith("/leave-request") || pathname.startsWith("/field-duty") || pathname.startsWith("/my-requests")) return null;
  return (
    <>
      <ChatLauncher />
      <EnquiryChatModal />
      <BackToTop />
      <CookieConsent />
    </>
  );
};


function App() {
  useEffect(() => {
    // Lenis smooth-scroll is for the marketing site only — it hijacks the wheel and breaks native scrolling inside NexSecure Connect
    if (window.location.pathname.startsWith("/connect") || window.location.pathname.startsWith("/privacy-policy") || window.location.pathname.startsWith("/delete-account")) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    setLenis(lenis);
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      setLenis(null);
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollManager />
        <PageTracker />
        <RouteSeo />
        <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path={BLUE_PREVIEW_PATH} element={<HomePan />} />
          <Route path="/staff" element={<StaffGate />} />
          <Route path="/accept/:token" element={<Accept />} />
          <Route path="/solutions" element={<SolutionsIndex />} />
          <Route path="/solutions/:slug" element={<SolutionDetail />} />
          <Route path="/services" element={<ServicesIndex />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/industries" element={<IndustriesIndex />} />
          <Route path="/industries/:slug" element={<IndustryDetail />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:slug" element={<ProjectDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<ArticleDetail />} />
          <Route path="/support" element={<Support />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/careers/replace/:token" element={<CareerReplace />} />
          <Route path="/resubmit/:token" element={<Resubmit />} />
          <Route path="/hold-upload/:token" element={<HoldUpload />} />
          <Route path="/upload-docs/:token" element={<DocRequestUpload />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/consultation" element={<Consultation />} />
          <Route path="/technology" element={<Technology />} />
          <Route path="/downloads" element={<Downloads />} />
          <Route path="/legal/:slug" element={<Legal />} />
          <Route path="/request-a-quote" element={<RequestQuote />} />
          <Route path="/inquiries" element={<Inquiries />} />
          <Route path="/our-team" element={<Team />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/brands-we-deal-with" element={<Brands />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/payment/success" element={<PaymentResult success={true} />} />
          <Route path="/payment/cancel" element={<PaymentResult success={false} />} />
          <Route path="/payment/failure" element={<PaymentResult success={false} />} />
          <Route path="/pay/:txnid" element={<PaymentLink />} />
          <Route path="/receipts" element={<Receipts />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/payment-test" element={<PaymentTest />} />
          <Route path="/store" element={<Store />} />
          <Route path="/store/:slug" element={<StoreCategory />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/webmail" element={<Webmail />} />
          <Route path="/connect/*" element={<ConnectApp />} />
          <Route path="/privacy-policy" element={<ConnectPrivacy />} />
          <Route path="/delete-account" element={<ConnectDeleteAccount />} />
          <Route path="/connect-privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/onboarding/:token" element={<OnboardingPortal />} />
          <Route path="/attendance-correction" element={<RegularizationRequest />} />
          <Route path="/leave-request" element={<LeaveRequest />} />
          <Route path="/field-duty" element={<FieldDuty />} />
          <Route path="/my-requests" element={<MyRequests />} />
          <Route path="/concepts" element={<ConceptPicker />} />
          <Route path="/concept-2" element={<ConceptTwo />} />
          <Route path="/concept-3" element={<ConceptThree />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </ErrorBoundary>
        <PublicChrome />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
